import { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material'
import {
  Assessment,
  TrendingUp,
  People,
  Folder,
  VideoCall,
  Download,
  Description,
  Calculate,
  Campaign,
  CheckCircle,
  Cancel,
} from '@mui/icons-material'
import {
  useGetReportsDashboardQuery,
  useLazyExportScoresCsvQuery,
  useLazyExportSummaryTxtQuery,
  useLazyExportSummaryPdfQuery,
  useLazyGetRoundCompletionQuery,
  useLazyGetRankingQuery,
  usePublishRoundMutation,
} from '../store/api/reportsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetRoundsQuery } from '../store/api/roundsApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { PageHeader } from '../components/PageHeader'
import { getCardSurfaceSx, getContentPaperSx, getTablePaperSx, getFilterPaperSx } from '../theme/sectionSurfaces'
import { gradientBrand } from '../theme/brand'

export default function ReportsPage() {
  const [tab, setTab] = useState<0 | 1>(0)
  const { data, isLoading, isError, error } = useGetReportsDashboardQuery()
  const [exportCsv] = useLazyExportScoresCsvQuery()
  const [exportTxt] = useLazyExportSummaryTxtQuery()
  const [exportPdf] = useLazyExportSummaryPdfQuery()

  // Round/case filter for tab 1
  const { data: cases = [] } = useGetCasesQuery()
  const { data: rounds = [] } = useGetRoundsQuery()
  const { data: teams = [] } = useGetTeamsQuery()
  const [selectedCaseId, setSelectedCaseId] = useState('')
  const [selectedRoundId, setSelectedRoundId] = useState<string>('')   // '' = tất cả vòng
  const [getCompletion, { data: completion, isFetching: completionLoading }] =
    useLazyGetRoundCompletionQuery()
  const [getRanking, { data: ranking, isFetching: rankingLoading }] = useLazyGetRankingQuery()
  const [publishRound, { isLoading: publishing }] = usePublishRoundMutation()

  // Publish dialog state
  const [publishOpen, setPublishOpen] = useState(false)
  const [advancingTeams, setAdvancingTeams] = useState<string[]>([])
  const [publishTitle, setPublishTitle] = useState('')
  const [publishNote, setPublishNote] = useState('')

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'info',
  })
  const notify = (message: string, severity: typeof snackbar.severity) =>
    setSnackbar({ open: true, message, severity })

  const caseRounds = useMemo(
    () => rounds.filter((r: any) => r.case_id === selectedCaseId),
    [rounds, selectedCaseId]
  )

  const handleExportCsv = async () => {
    try {
      const blob = await exportCsv().unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'scores_export.csv'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Xuất CSV thất bại', 'error')
    }
  }

  const handleExportTxt = async () => {
    try {
      const text = await exportTxt().unwrap()
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'summary.txt'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Xuất TXT thất bại', 'error')
    }
  }

  const handleExportPdf = async () => {
    try {
      const blob = await exportPdf().unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'summary.pdf'
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Xuất PDF thất bại', 'error')
    }
  }

  // UAT-TC #15: Tính điểm & Xếp hạng
  const handleCalculateRanking = async () => {
    if (!selectedCaseId) {
      notify('Vui lòng chọn cuộc thi', 'warning')
      return
    }
    try {
      // Kiểm tra giám khảo đã chấm xong (chỉ check khi đã chọn round cụ thể)
      if (selectedRoundId) {
        const c = await getCompletion({ case_id: selectedCaseId, round_id: selectedRoundId }).unwrap()
        if (!c.complete) {
          notify(
            `Mới có ${c.submitted_count}/${c.expected_total} phiếu được gửi — vẫn xếp hạng theo dữ liệu hiện có.`,
            'warning'
          )
        }
      }
      await getRanking({ case_id: selectedCaseId, round_id: selectedRoundId || null }).unwrap()
      notify('Đã tính điểm & cập nhật bảng xếp hạng.', 'success')
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Tính điểm thất bại', 'error')
    }
  }

  // UAT-TC #16 / Bán kết #11 / Chung kết #18: Công bố kết quả
  const handleOpenPublish = () => {
    if (!selectedCaseId || !selectedRoundId) {
      notify('Vui lòng chọn cuộc thi và vòng cụ thể trước khi công bố.', 'warning')
      return
    }
    if (!ranking || ranking.ranking.length === 0) {
      notify('Vui lòng tính điểm xếp hạng trước.', 'warning')
      return
    }
    setAdvancingTeams([])
    const round = caseRounds.find((r: any) => r.id === selectedRoundId)
    setPublishTitle(`Kết quả vòng ${round?.name || selectedRoundId}`)
    setPublishNote('')
    setPublishOpen(true)
  }

  const handleConfirmPublish = async () => {
    try {
      const result = await publishRound({
        case_id: selectedCaseId,
        round_id: selectedRoundId,
        advancing_team_ids: advancingTeams,
        title: publishTitle,
        note: publishNote,
      }).unwrap()
      notify(
        `Đã công bố kết quả. Đã gửi thông báo + email cho ${result.notified_teams} đội.`,
        'success'
      )
      setPublishOpen(false)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Công bố kết quả thất bại', 'error')
    }
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title="Báo cáo và Phân tích"
        subtitle="Tổng quan điểm số, xếp hạng theo vòng, công bố kết quả."
        icon={<Assessment />}
        mb={3}
        actions={
          <>
            <Button variant="outlined" color="primary" startIcon={<Download />} onClick={handleExportCsv}>
              Xuất CSV điểm
            </Button>
            <Button variant="outlined" color="primary" startIcon={<Description />} onClick={handleExportTxt}>
              Xuất TXT
            </Button>
            <Button variant="contained" color="primary" startIcon={<Download />} onClick={handleExportPdf}>
              Xuất PDF
            </Button>
          </>
        }
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Tổng quan" />
        <Tab label="Vòng & Xếp hạng" />
      </Tabs>

      {tab === 0 && (
        <>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {isError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {(error as any)?.data?.detail || 'Không tải được thống kê.'}
            </Alert>
          )}
          {data && (
            <>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Cuộc thi
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">{data.total_cases}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}><Folder /></Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Tổng đội
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">{data.total_teams}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}><People /></Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Phiên courtroom
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">{data.total_courtrooms}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}><VideoCall /></Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Điểm TB
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">
                            {data.average_total_score != null ? data.average_total_score : '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Phiếu đã gửi: {data.submitted_scores_count}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}><TrendingUp /></Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Paper elevation={0} sx={(theme) => ({ ...getContentPaperSx(theme), p: 3 })}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Bảng xếp hạng tổng (theo điểm TB phiếu đã gửi)
                </Typography>
                <TableContainer sx={(theme) => getTablePaperSx(theme)}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Đội</TableCell>
                        <TableCell align="right">Điểm TB</TableCell>
                        <TableCell align="right">Số phiếu</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data.leaderboard || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography color="text.secondary">Chưa có dữ liệu xếp hạng.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.leaderboard.map((row, idx) => (
                          <TableRow key={row.team_id} hover>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{row.team_name}</TableCell>
                            <TableCell align="right">
                              <Chip label={row.avg_score} size="small" color="primary" />
                            </TableCell>
                            <TableCell align="right">{row.score_count}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          {/* Filter: Cuộc thi + Vòng */}
          <Paper
            elevation={0}
            sx={(t) => ({ ...getFilterPaperSx(t), display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' })}
          >
            <FormControl sx={{ minWidth: 240, flex: 1 }} size="small">
              <InputLabel>Cuộc thi</InputLabel>
              <Select
                value={selectedCaseId}
                label="Cuộc thi"
                onChange={(e) => {
                  setSelectedCaseId(e.target.value)
                  setSelectedRoundId('')
                }}
              >
                {cases.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 220 }} size="small" disabled={!selectedCaseId}>
              <InputLabel>Vòng</InputLabel>
              <Select
                value={selectedRoundId}
                label="Vòng"
                onChange={(e) => setSelectedRoundId(e.target.value)}
              >
                <MenuItem value=""><em>Tổng hợp tất cả vòng</em></MenuItem>
                {caseRounds.map((r: any) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name} ({r.round_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<Calculate />}
              onClick={handleCalculateRanking}
              disabled={!selectedCaseId || rankingLoading || completionLoading}
              sx={{ background: gradientBrand, textTransform: 'none' }}
            >
              {rankingLoading || completionLoading ? 'Đang tính...' : 'Tính điểm & Xếp hạng'}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<Campaign />}
              onClick={handleOpenPublish}
              disabled={!selectedCaseId || !selectedRoundId || !ranking}
              sx={{ textTransform: 'none' }}
            >
              Công bố kết quả
            </Button>
          </Paper>

          {/* Trạng thái chấm điểm của giám khảo */}
          {selectedRoundId && completion && (
            <Paper elevation={0} sx={(t) => ({ ...getContentPaperSx(t), mb: 3 })}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Tiến độ chấm điểm vòng
                </Typography>
                <Chip
                  icon={completion.complete ? <CheckCircle /> : <Cancel />}
                  label={
                    completion.complete
                      ? `Hoàn thành ${completion.submitted_count}/${completion.expected_total}`
                      : `Đang chấm ${completion.submitted_count}/${completion.expected_total}`
                  }
                  color={completion.complete ? 'success' : 'warning'}
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Giám khảo</strong></TableCell>
                      <TableCell align="right"><strong>Đã chấm</strong></TableCell>
                      <TableCell align="right"><strong>Cần chấm</strong></TableCell>
                      <TableCell align="right"><strong>Trạng thái</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {completion.judges.map((j) => (
                      <TableRow key={j.judge_id}>
                        <TableCell>{j.judge_id}</TableCell>
                        <TableCell align="right">{j.done}</TableCell>
                        <TableCell align="right">{j.expected}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={j.complete ? 'Xong' : 'Đang chấm'}
                            color={j.complete ? 'success' : 'warning'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Bảng xếp hạng theo vòng */}
          {ranking && (
            <Paper elevation={0} sx={(t) => getContentPaperSx(t)}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Xếp hạng — {selectedRoundId ? 'theo vòng đã chọn' : 'tổng hợp các vòng'}
              </Typography>
              <TableContainer sx={(t) => getTablePaperSx(t)}>
                <Table size="small" sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 60 }}>#</TableCell>
                      <TableCell sx={{ minWidth: 200 }}><strong>Đội</strong></TableCell>
                      <TableCell align="right" sx={{ minWidth: 100 }}><strong>Điểm TB</strong></TableCell>
                      <TableCell align="right" sx={{ minWidth: 100 }}><strong>Tổng điểm</strong></TableCell>
                      <TableCell align="right" sx={{ minWidth: 100 }}><strong>Số phiếu</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ranking.ranking.map((r) => (
                      <TableRow key={r.team_id} hover>
                        <TableCell>
                          <Chip
                            label={`#${r.rank}`}
                            size="small"
                            color={r.rank === 1 ? 'warning' : r.rank <= 3 ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{r.team_name}</TableCell>
                        <TableCell align="right">{r.average_score?.toFixed(2)}</TableCell>
                        <TableCell align="right">{r.total_score?.toFixed(2)}</TableCell>
                        <TableCell align="right">{r.judge_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* Dialog: Công bố kết quả */}
      <Dialog open={publishOpen} onClose={() => setPublishOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Công bố kết quả vòng</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Sau khi công bố, hệ thống sẽ gửi email + thông báo cho TẤT CẢ đội trong cuộc thi
            (đội qua vòng nhận thông báo "Chúc mừng", đội còn lại nhận thông báo cảm ơn).
          </Alert>
          <TextField
            label="Tiêu đề công bố"
            value={publishTitle}
            onChange={(e) => setPublishTitle(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Ghi chú thêm"
            value={publishNote}
            onChange={(e) => setPublishNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Đội qua vòng</InputLabel>
            <Select
              multiple
              value={advancingTeams}
              onChange={(e) => setAdvancingTeams(e.target.value as string[])}
              label="Đội qua vòng"
              renderValue={(sel) =>
                (sel as string[]).map((id) => teams.find((t) => t.id === id)?.name || id).join(', ')
              }
            >
              {(ranking?.ranking || []).map((r) => (
                <MenuItem key={r.team_id} value={r.team_id}>
                  <Checkbox checked={advancingTeams.indexOf(r.team_id) > -1} />
                  <ListItemText
                    primary={`#${r.rank} — ${r.team_name}`}
                    secondary={`Điểm TB: ${r.average_score?.toFixed(2)}`}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)}>Huỷ</Button>
          <Button
            onClick={handleConfirmPublish}
            variant="contained"
            disabled={publishing || advancingTeams.length === 0}
          >
            {publishing ? 'Đang công bố...' : 'Xác nhận công bố'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
